import { useEffect, useState } from 'react';
import { useOperator } from '../../contexts/OperatorContext';
import { BASE_URL_MAP } from '../../lib/environment';
import { sanitizeValue } from '../../lib/sanitize';
import {
  ApiSupportTicket,
  ApiSupportTicketRequest,
  ApiSupportTicketUpdate,
  ApiSupportTicketUpdateAttachmentRequest,
  ApiSupportTicketUpdateMessage,
} from '../../types/apiTypes';
import {
  SupportCreateTicket,
  SupportTicket,
  SupportTicketUpdate,
  SupportTicketUpdateMessage,
} from '../../types/commonTypes';
import { useFetch } from '../useFetch';
import { useSupportTicketCategories } from './useSupportTicketCategories';

export const useSupportTicket = (getCategories?: boolean) => {
  const categories = useSupportTicketCategories(getCategories ?? true);
  const [action, setAction] = useState<
    'getTickets' | 'updateTicket' | 'createTicket' | null
  >(null);
  const [tickets, setTickets] = useState<SupportTicket[]>([]);
  const [ticketId, setTicketId] = useState<number | undefined>();
  const { error, loading, makeRequest, response } = useFetch();
  const operator = useOperator();
  const BASE_URL = `${BASE_URL_MAP[operator.type]}/${
    operator.id
  }/SupportTicket`;

  useEffect(() => {
    if (!loading && response) {
      if (action === 'getTickets') {
        setTickets(
          response.Tickets.map((ticket: ApiSupportTicket) => mapTicket(ticket))
        );
      } else if (action === 'updateTicket') {
        setTickets([
          mapTicket(response),
          ...tickets.filter((ticket) => ticket.id !== ticketId),
        ]);
      }
      resetState();
    }
  }, [loading, response]);

  const addMessage = (
    id: number,
    message: string,
    ccList: string,
    attachment?: ApiSupportTicketUpdateAttachmentRequest
  ) => {
    setTicketId(id);
    setAction('updateTicket');

    const body = sanitizeValue({
      Attachment: attachment ? attachment : null,
      Cc: ccList,
      Text: message,
    });

    makeRequest(`${BASE_URL}/${id}`, 'put', body);
  };

  const createTicket = (ticket: SupportCreateTicket) => {
    setAction('createTicket');
    const siteId = ticket.site ? ticket.site.toLowerCase() : null;
    let url = BASE_URL;
    if (siteId && siteId !== 'all') url += `?accountSiteId=${siteId}`;

    const newTicket = mapNewTicket(ticket);
    const body = sanitizeValue(newTicket);

    makeRequest(url, 'post', body);
  };

  const getTickets = (
    pending: boolean,
    startDate: Date,
    endDate: Date,
    siteId?: string | null,
    page?: string
  ) => {
    setAction('getTickets');
    let url = `${BASE_URL}?pending=${pending}`;
    if (!pending) {
      url += `&startDate=${startDate.toISOString()}&endDate=${endDate.toISOString()}`;
    }
    if (siteId && siteId.toLowerCase() !== 'all')
      url += `&accountSiteId=${siteId}`;
    if (page) url += `&page=${page}`;

    makeRequest(url, 'get');
  };

  const resetState = () => {
    setAction(null);
    setTicketId(undefined);
  };

  return {
    addMessage,
    categories,
    createTicket,
    error,
    getTickets,
    loading,
    response,
    tickets,
  };
};

const determineLastUpdatedBy = (updates: ApiSupportTicketUpdate[]): string => {
  let lastUpdatedBy = 'N/A';
  if (updates.length) {
    const lastUpdate = updates[updates.length - 1];
    if (lastUpdate.Type.toLowerCase() === 'staff') lastUpdatedBy = 'TG Support';
    else lastUpdatedBy = lastUpdate.By;
  }
  return lastUpdatedBy;
};

const mapNewTicket = (ticket: SupportCreateTicket): ApiSupportTicketRequest => {
  return {
    Attachment: ticket.attachment
      ? {
          FileName: ticket.attachment.filename,
          Base64: ticket.attachment.base64,
        }
      : null,
    Category: ticket.category,
    Cc: ticket.cc,
    Name: ticket.name,
    Phone: ticket.phone,
    Subcategories: ticket.subcategories.map((c) => ({
      ID: c.id,
      Value: c.value,
    })),
    Subject: ticket.subject,
    Text: ticket.text,
  };
};

const mapTicket = (ticket: ApiSupportTicket): SupportTicket => {
  return {
    id: ticket.ID,
    categoryName: ticket.CategoryName,
    createdAt: new Date(ticket.CreatedAt).toLocaleString(),
    customFields: ticket.CustomFields.map((field) => ({
      name: field.Name,
      value: field.Value,
    })),
    displayID: ticket.DisplayID,
    firstMessage: ticket.FirstMessage,
    priority: ticket.Priority,
    behavior: ticket.Behavior,
    subject: ticket.Subject,
    updates: ticket.Updates.map((update) => mapUpdate(update)),
    lastUpdatedAt: new Date(
      ticket.Updates[ticket.Updates.length - 1].Timestamp
    ).toLocaleString(),
    lastUpdatedBy: determineLastUpdatedBy(ticket.Updates),
    user: ticket.User,
  };
};

const mapUpdate = (update: ApiSupportTicketUpdate): SupportTicketUpdate => {
  return {
    by: update.By,
    message: mapUpdateMessage(update.Message),
    timestamp: new Date(update.Timestamp).toLocaleString(),
    type: update.Type,
  };
};

const mapUpdateMessage = (
  message: ApiSupportTicketUpdateMessage
): SupportTicketUpdateMessage => {
  return {
    attachments: message.Attachments.map((attachment) => ({
      filename: attachment.Filename,
      id: attachment.ID,
      url: attachment.Url,
    })),
    ccList: message.CcList,
    html: message.Html,
    subject: message.Subject,
  };
};
